import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Ledger } from '../ledger.types';
import { ImportsService as ImportsServiceV2 } from 'ldt-ledger-service-api-v2';
import * as FileSaver from 'file-saver';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { DeleteConfirmationComponent } from 'src/app/shared/components/delete-confirmation/delete-confirmation.component';
import { DatePipe, formatNumber } from '@angular/common';
import { AuthService } from 'src/app/auth/service/auth.service';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';

@Component({
    selector: 'app-ledger-import',
    templateUrl: './ledger-import.component.html',
    styleUrls: ['./ledger-import.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class LedgerImportComponent implements OnInit {
  @Output() refreshContactRequest: EventEmitter<any> = new EventEmitter();
  canEdit: boolean = false;

  // TODO

  rowsSelected = false;

  private simpleFilterParams: any = {
    filterOptions: ['contains'],
    suppressAndOrCondition: true,
  };
  defaultColDef = {
    resizable: true,
    sortable: false,
    filter: false,
    floatingFilter: true,
    filterParams: this.simpleFilterParams,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
  };
  private statusFilterParams: any = {
    filterOptions: [
      'empty',
      {
        displayKey: 'importing',
        displayName: 'Importing',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'false';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'processing',
        displayName: 'Processing',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'completed',
        displayName: 'Completed',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'deleted',
        displayName: 'Deleted',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'failed',
        displayName: 'Failed',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
    ],
    suppressAndOrCondition: true,
  };
  private typeFilterParams: any = {
    filterOptions: [
      'empty',
      {
        displayKey: 'fileUpload',
        displayName: 'File Upload',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'false';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'manualInput',
        displayName: 'Manual Input',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
      {
        displayKey: 'apiImport',
        displayName: 'API',
        test: function (filterValue: any, cellValue: any) {
          return cellValue === 'true';
        },
        hideFilterInput: true,
      },
    ],
    suppressAndOrCondition: true,
  };
  columnDefs = [
    {
      field: 'created_at',
      sortable: true,
      headerName: 'Started At (UTC)',
      sort: 'desc',
      valueFormatter: (params: any) => {
        return this.datePipe.transform(params.value, 'yyyy-MM-dd h:mm a', 'UTC');
      },
      headerTooltip: 'Time that the import was started',
    },
    {
      field: 'status',
      maxWidth: 100,
      filter: 'agTextColumnFilter',
      sortable: true,
      cellRenderer: 'loadingRenderer',
      filterParams: this.statusFilterParams,
      headerTooltip: 'Imports are in progress until showing "completed"',
    },
    {
      headerName: 'Import Details',
      children: [
        {
          headerName: 'Submitted',
          maxWidth: 120,
          valueGetter: (params: any) => {
            if (!params.data) {
              return '';
            }
            return formatNumber(
              (params.data.count_imported || 0) +
                (params.data.count_duplicates || 0) +
                (params.data.count_failures || 0),
              this.locale
            );
          },
          headerTooltip: 'The total number of records submitted in this import job',
        },
        {
          field: 'count_imported',
          headerName: 'Imported',
          maxWidth: 120,
          headerTooltip: 'The number of records imported to the ledger from this import job',
          valueFormatter: (params: any) => {
            return formatNumber(params.value, this.locale);
          },
        },
        {
          field: 'count_duplicates',
          headerName: 'Duplicates',
          maxWidth: 120,
          headerTooltip:
            'The number of people that were detected as already on the ledger and ignored from this import job',
          valueFormatter: (params: any) => {
            return formatNumber(params.value, this.locale);
          },
        },
        {
          field: 'count_failures',
          headerName: 'Failed',
          maxWidth: 120,
          headerTooltip: 'The number of records that failed to import',
          valueFormatter: (params: any) => {
            return formatNumber(params.value, this.locale);
          },
        },
      ],
      headerTooltip: 'Details of this import job',
    },
    {
      field: 'import_type',
      filter: 'agTextColumnFilter',
      sortable: true,
      filterParams: this.typeFilterParams,
      headerTooltip: 'The type of import (file, manual, or API)',
    },
    {
      field: 'finished_at',
      headerName: 'Finished At (UTC)',
      headerTooltip: 'Time at which the import finished',
      valueFormatter: (params: any) => {
        return this.datePipe.transform(params.value, 'yyyy-MM-dd h:mm a', 'UTC');
      },
    },
    {
      // The v2 API only returns the submitting user's ID
      field: 'imported_by',
      headerName: 'Imported By',
      headerTooltip: 'ID of the user that submitted this import job',
    },
  ];
  tooltipShowDelay = 200;

  gridApi: any;
  rowModelType: any = 'infinite';
  paginationPageSize = 100;
  infiniteInitialRowCount = 1;
  rowSelection = {
    mode: 'singleRow',
    isRowSelectable: this.isRowSelectable,
    enableClickSelection: true,
  };
  components = {
    loadingRenderer: function (params: any) {
      if (params.value !== undefined) {
        return params.value;
      } else {
        return '<img src="https://www.ag-grid.com/example-assets/loading.gif">';
      }
    },
  };

  refreshing: boolean = true;

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    public importService: ImportsServiceV2,
    public dialog: MatDialog,
    public route: ActivatedRoute,
    private notify: NotificationService,
    private datePipe: DatePipe,
    private auth: AuthService,
    public darkModeService: DarkModeService
  ) {}

  ledger: Ledger;
  orgId: string;
  async ngOnInit() {
    // Get the ledger from the resolver and org ID from the route
    this.ledger = this.route.parent?.snapshot.data.userdata;
    let routeOrg = this.route.parent?.snapshot.paramMap.get('orgId');
    if (routeOrg) {
      this.orgId = routeOrg;
    }
    this.canEdit = this.auth.userHasRole('editor');
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.setGridOption('datasource', this);
    this.gridApi.sizeColumnsToFit();
  }

  isRowSelectable(rowNode: any) {
    // backwards compatibility for status value
    return rowNode.data
      ? rowNode.data.status === 'completed' ||
          rowNode.data.status === 'complete' ||
          rowNode.data.status === 'processing'
      : false;
  }

  getRows(params: any): any {
    // Set the offset and limit
    var queryModel = {
      offset: params.startRow,
      limit: params.endRow - params.startRow,
    };

    var sortBy;
    if (params.sortModel.length > 0) {
      var dir = '';
      if (params.sortModel[0].sort == 'desc') {
        dir = '-';
      }
      sortBy = dir + params.sortModel[0].colId;
    }

    // Get the data
    this.importService
      .getLedgerImports(
        this.orgId,
        this.ledger.id,
        params.filterModel['filename'] ? params.filterModel['filename'].filter : undefined,
        params.filterModel['status'] ? params.filterModel['status'].type : undefined,
        params.filterModel['import_type'] ? params.filterModel['import_type'].type : undefined,
        sortBy,
        params.endRow - params.startRow,
        params.startRow
      )
      .subscribe(
        (res) => {
          const imports = res.imports || [];
          var lastRow = -1;
          if (imports.length < queryModel.limit) {
            lastRow = queryModel.offset + imports.length;
          }
          params.successCallback(imports, lastRow);
          this.refreshing = false;
        },
        () => {
          params.failCallback();
          this.notify.error(
            'Oops. There was an error during your request. Please try again later.'
          );
          this.refreshing = false;
        }
      );
  }

  refreshData() {
    this.refreshing = true;
    this.gridApi.purgeInfiniteCache();
  }

  deleteSelectedImport() {
    var ids: any = this.gridApi.getSelectedRows().map((d: any) => d.ledger_import_id);
    const confirmDialog = this.dialog.open(DeleteConfirmationComponent, {
      data: {
        title: 'Confirm Contacts Deletion',
        message:
          'Are you sure you want to delete ALL contacts imported by this import? This action is unrecoverable!',
      },
    });
    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        this.importService.deleteLedgerImport(this.orgId, this.ledger.id, ids[0]).subscribe(
          (response) => {
            this.gridApi.deselectAll();
            this.gridApi.purgeInfiniteCache();
            this.refreshContactRequest.emit();
            this.notify.success(
              response.count_contacts_deleted + ' contacts successfully deleted.'
            );
          },
          () => {
            this.notify.error(
              'Oops. There was an error during your request. Please try again later.'
            );
          }
        );
      }
    });
  }

  onSelectionChanged(event: any) {
    var selectedRows = this.gridApi.getSelectedRows();
    this.rowsSelected = selectedRows.length !== 0;
  }

  downloadErrors(e: any) {
    if (
      e.data.status == 'completed' &&
      (e.data.count_duplicates > 0 || e.data.count_failures > 0)
    ) {
      this.importService
        .downloadLedgerImportErrors(this.orgId, this.ledger.id, e.data.ledger_import_id)
        .subscribe({
          next: (res: any) => {
            var data = new Blob([res], { type: 'text/csv' });
            FileSaver.saveAs(data, e.data.filename + '-livedata-import-errors' + '.csv');
          },
          error: () => {
            this.notify.error(
              'Oops. There was an error during your request. Please try again later.'
            );
          },
        });
    }
  }
}
